import { api } from "./api";
import type { UnpublishPreview } from "./unpublish-consequences";

export interface ProfilePublishResponse {
  profile: {
    id: string;
    portal_discoverable: boolean;
    published_at?: string | null;
    publish_requested_at?: string | null;
    target_area?: string | null;
    buyer_type?: string | null;
    timeline?: string | null;
  };
  // M11-B2 (plan 325): present on the unpublish direction only — what the
  // exit-market routine withdrew/closed (counts, never ids).
  exit_market?: {
    withdrawn_requests: number;
    closed_offers: number;
    closed_windows: number;
  };
}

interface UnpublishPreviewWire {
  pending_requests: number;
  open_offers: number;
  open_windows: number;
}

// getProfileUnpublishPreview is the consequence dialog's preflight: the
// same three lookups the exit-market routine runs, with no writes.
export async function getProfileUnpublishPreview(
  getToken: () => Promise<string | null>,
): Promise<UnpublishPreview> {
  const token = await getToken();
  const w = await api.get<UnpublishPreviewWire>(
    "/api/profile/unpublish/preview",
    token ?? undefined,
  );
  return {
    pendingRequests: w.pending_requests,
    openOffers: w.open_offers,
    openWindows: w.open_windows,
  };
}

export interface PublishRequirement {
  code: string;
  group: "contact" | "goals" | "inputs" | string;
  label: string;
  href?: string;
}

export interface PublishWarning {
  code: string;
  label: string;
  detail?: string;
}

export interface PublishContactStatus {
  namePresent: boolean;
  emailPresent: boolean;
  emailVerified: boolean;
  phonePresent: boolean;
  phoneVerified: boolean;
}

export interface PublishAnonymousPreview {
  goals: {
    buyerType?: string;
    targetArea?: string;
    targetPrice?: number | null;
    timeline?: string;
    monthlyComfort?: number | null;
  };
  financials: {
    householdIncome?: number | null;
    cashToClose?: number | null;
    monthlyDebts?: number | null;
    credit?: string;
    employmentMonths?: number | null;
    estimatedMaxPurchasePrice?: number | null;
  };
  context: {
    housingSituation?: string;
  };
  verification: {
    contact?: string;
    financials?: string;
    readiness?: string;
  };
}

export interface PublishEligibility {
  eligible: boolean;
  missing: PublishRequirement[];
  warnings: PublishWarning[];
  contact: PublishContactStatus;
  preview: PublishAnonymousPreview;
  request?: {
    submitted: boolean;
    requestedAt?: string | null;
    portalDiscoverable: boolean;
    workflowStatus?: "submitted" | "reviewing" | "matching" | "connected" | null;
  };
}

export interface PublishRequestInput {
  shareFinancialData: boolean;
  shareGoals: boolean;
  shareReadinessSummary: boolean;
  notes?: string;
  consentVersion: string;
}

export interface PublishRequestResponse extends ProfilePublishResponse {
  eligibility: PublishEligibility;
}

export async function putProfilePublish(
  getToken: () => Promise<string | null>,
  published: boolean,
): Promise<ProfilePublishResponse> {
  const token = await getToken();
  return api.put<ProfilePublishResponse>(
    "/api/profile/publish",
    { published },
    token ?? undefined,
  );
}

export async function getProfilePublishEligibility(
  getToken: () => Promise<string | null>,
): Promise<PublishEligibility> {
  const token = await getToken();
  return api.get<PublishEligibility>(
    "/api/profile/publish/eligibility",
    token ?? undefined,
  );
}

export async function postProfilePublishRequest(
  getToken: () => Promise<string | null>,
  input: PublishRequestInput,
): Promise<PublishRequestResponse> {
  const token = await getToken();
  return api.post<PublishRequestResponse>(
    "/api/profile/publish/request",
    input,
    token ?? undefined,
  );
}
